import React, { useState, useEffect } from "react"
import { ArrowRightIcon, AlertCircle, CheckCircle, Upload } from "lucide-react"
import { 
  TextField, 
  Button, 
  Box, 
  Typography, 
  Paper, 
  Alert, 
  AlertTitle,
  FormControl,
  FormLabel,
  Stack,
  CircularProgress,
  Divider,
  Chip
} from "@mui/material"
import { History as HistoryIcon } from "@mui/icons-material"
import Module1Service from "../../services/module1Service"
import PromptEditor from "../PromptEditor"
import { snowflakeApi } from "../../utils/api"

interface InputFormContainerProps {
  className?: string
  onAnalysisComplete?: (results: any) => void
  hasUploadedFile?: boolean
  hasSnowflakeKeywords?: boolean
  isSubmitting?: boolean
  snowflakeBlobPath?: string | null
  snowflakeKeywordCount?: number | null
  hideKeywordsSource?: boolean
  workflowId?: string | null // Add workflowId prop
  workflow?: any // Add workflow object prop
}

interface FormData {
  workflowId?: string;
  businessName: string;
  industry: string;
  description: string;
  targetAudience: string;
  additionalContext: string;
  customPrompt: string;
  keywordLimit?: number;
}

const InputFormContainer: React.FC<InputFormContainerProps> = ({ 
  className, 
  onAnalysisComplete, 
  hasUploadedFile = false,
  hasSnowflakeKeywords = false,
  isSubmitting = false,
  snowflakeBlobPath = null,
  snowflakeKeywordCount = null,
  hideKeywordsSource = false,
  workflowId = null,
  workflow = null
}) => {
  const [formData, setFormData] = useState<FormData>({
    businessName: "",
    industry: "",
    description: "",
    targetAudience: "",
    additionalContext: "",
    customPrompt: "",
    keywordLimit: undefined
  });

  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // CRITICAL: Immediately reset form data when workflowId changes (before any async operations)
  // This runs synchronously and prevents stale data from appearing
  useEffect(() => {
    console.log(`InputFormContainer: workflowId changed to ${workflowId}, immediately resetting form`);
    setFormData({
      businessName: "",
      industry: "",
      description: "",
      targetAudience: "",
      additionalContext: "",
      customPrompt: "",
      keywordLimit: undefined
    });
    setConfigLoaded(false);
  }, [workflowId]);

  // Effect to load saved business configuration
  useEffect(() => {
    const loadBusinessConfig = async () => {
      console.log(`InputFormContainer: Loading business config for workflowId=${workflowId}, workflow?.workflow_id=${workflow?.workflow_id}`);
      
      // Only load if we have a workflow with numeric workflow_id
      if (!workflow?.workflow_id) {
        console.log('InputFormContainer: No workflow.workflow_id available, keeping form empty');
        return;
      }
      
      // Safety check: Ensure the workflow object matches the current workflowId
      // This prevents using stale workflow data from React context
      if (workflowId && workflow?.workflow_id?.toString() !== workflowId.toString()) {
        console.log(`InputFormContainer: workflow_id mismatch! workflow.workflow_id=${workflow.workflow_id} vs workflowId=${workflowId}. Skipping load to prevent stale data.`);
        return;
      }
      
      try {
        setIsLoadingConfig(true);
        const response = await snowflakeApi.get(
          `/api/v1/db/workflow/${workflow.workflow_id}/business-config`
        );
        
        if (response.data.business_config && Object.keys(response.data.business_config).length > 0) {
          const config = response.data.business_config;
          
          console.log('InputFormContainer: Found business config, populating form', config);
          // Pre-populate form fields with saved data
          setFormData({
            businessName: config.business_name || '',
            industry: config.industry || '',
            description: config.description || '',
            targetAudience: config.target_audience || '',
            additionalContext: config.additional_context || '',
            customPrompt: "",
            keywordLimit: undefined
          });
          
          setConfigLoaded(true);
        } else {
          console.log('InputFormContainer: No business config found, form remains empty');
        }
      } catch (error) {
        // Silently fail - it's okay if no config exists yet
        console.log('No previous business config found or error loading:', error);
      } finally {
        setIsLoadingConfig(false);
      }
    };
    
    loadBusinessConfig();
  }, [workflowId, workflow?.workflow_id]);

  // Effect to check if form data is complete
  useEffect(() => {
    const missing = [];
    if (!formData.businessName.trim()) missing.push("Business name");
    if (!formData.description.trim()) missing.push("Business description");
    // Modified validation: accept either file upload OR Snowflake keywords
    if (!hasUploadedFile && !hasSnowflakeKeywords) missing.push("Keyword file upload or Snowflake keywords");
    
    setMissingFields(missing);
    setIsComplete(missing.length === 0);
  }, [formData, hasUploadedFile, hasSnowflakeKeywords]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!isComplete || isSubmitting) return;

    console.log("Form data submitted:", formData);
    
    // Include snowflake data if keywords were loaded from Snowflake
    const submissionData = {
      ...formData,
      ...(hasSnowflakeKeywords && snowflakeBlobPath ? {
        keywordSource: 'snowflake',
        blobPath: snowflakeBlobPath,
        keywordCount: snowflakeKeywordCount
      } : {})
    };
    
    if (onAnalysisComplete) {
      onAnalysisComplete(submissionData);
    }
  };

  return (
    <Box 
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto'
      }}
    >
      <Stack spacing={3}>
        {/* Loading indicator */}
        {isLoadingConfig && (
          <Alert severity="info" icon={<CircularProgress size={20} />}>
            Loading previous business information...
          </Alert>
        )}
        
        {/* Data loaded indicator */}
        {configLoaded && !isLoadingConfig && (
          <Alert 
            severity="success" 
            icon={<HistoryIcon />}
            sx={{ mb: 1 }}
          >
            <AlertTitle>Previous Information Loaded</AlertTitle>
            <Typography variant="body2">
              Your business information from the last session has been restored. Feel free to update any fields before submitting.
            </Typography>
          </Alert>
        )}
        
        {/* Business Name */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>
            Business Name <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </FormLabel>
          <TextField
            id="businessName"
            name="businessName"
            value={formData.businessName}
            onChange={handleInputChange}
            placeholder="Enter your business name"
            size="small"
            fullWidth
          />
        </FormControl>

        {/* Industry */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>Industry</FormLabel>
          <TextField
            id="industry"
            name="industry"
            value={formData.industry}
            onChange={handleInputChange}
            placeholder="Enter your industry (optional)"
            size="small"
            fullWidth
          />
        </FormControl>

        {/* Description */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>
            Business Description <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </FormLabel>
          <TextField
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Enter a description of your business"
            multiline
            rows={3}
            fullWidth
          />
        </FormControl>

        {/* Target Audience */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>Target Audience</FormLabel>
          <TextField
            id="targetAudience"
            name="targetAudience"
            value={formData.targetAudience}
            onChange={handleInputChange}
            placeholder="Describe your target audience (optional)"
            multiline
            rows={2}
            fullWidth
          />
        </FormControl>

        {/* File Upload Reminder */}
        {!hideKeywordsSource && (
          <Alert severity={hasUploadedFile || hasSnowflakeKeywords ? "success" : "info"} icon={<Upload />}>
            <AlertTitle>Keywords Source</AlertTitle>
            <Typography variant="body2">
              {hasSnowflakeKeywords ? (
                <>
                  <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'success.main', fontWeight: 500 }}>
                    ✓ Keywords loaded from Snowflake
                    {snowflakeKeywordCount && (
                      <Box component="span" sx={{ ml: 1 }}>
                        ({snowflakeKeywordCount} keywords)
                      </Box>
                    )}
                  </Box>
                  {formData.keywordLimit && formData.keywordLimit < (snowflakeKeywordCount || 0) && (
                    <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'info.main', fontSize: '0.875rem' }}>
                      ℹ️ Only the first {formData.keywordLimit} keywords will be analyzed due to the keyword limit setting
                    </Box>
                  )}
                </>
              ) : hasUploadedFile ? (
                <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'success.main', fontWeight: 500 }}>
                  ✓ File has been uploaded
                </Box>
              ) : (
                <>
                  Please upload your keywords file in the file uploader section or load keywords from Snowflake.
                  <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'error.main', fontWeight: 500 }}>
                    × No keywords loaded yet
                  </Box>
                </>
              )}
            </Typography>
          </Alert>
        )}

        {/* Additional Context */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>Additional Context</FormLabel>
          <TextField
            id="additionalContext"
            name="additionalContext"
            value={formData.additionalContext}
            onChange={handleInputChange}
            placeholder="Any additional context that might help with analysis (optional)"
            multiline
            rows={3}
            fullWidth
          />
        </FormControl>

        {/* Divider before prompt editor */}
        <Divider sx={{ my: 2 }} />
        
        {/* Prompt Editor */}
        <Box>
          <Typography variant="subtitle1" fontWeight={500} mb={1}>
            Analysis Prompt Configuration
          </Typography>
          <PromptEditor
            module="module1"
            onPromptChange={(prompt) => {
              setFormData(prev => ({
                ...prev,
                customPrompt: prompt || ""
              }));
            }}
          />
        </Box>

        {/* Keyword Limit */}
        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.5, fontWeight: 500 }}>
            Keyword Limit
            <Box component="span" sx={{ ml: 1, fontSize: '0.875rem', color: 'text.secondary', fontWeight: 400 }}>
              (Optional - for testing with smaller sets)
            </Box>
          </FormLabel>
          <TextField
            id="keywordLimit"
            name="keywordLimit"
            type="number"
            value={formData.keywordLimit || ''}
            onChange={(e) => {
              const value = e.target.value ? parseInt(e.target.value, 10) : undefined;
              setFormData(prev => ({
                ...prev,
                keywordLimit: value
              }));
            }}
            placeholder="e.g., 100 (leave empty to use all keywords)"
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
            helperText="Limit the number of keywords sent for analysis. Useful for testing custom prompts with smaller datasets."
          />
        </FormControl>

        {/* Validation Status */}
        {missingFields.length > 0 ? (
          <Alert severity="warning">
            <AlertTitle>Please provide the following required information:</AlertTitle>
            <Box component="ul" sx={{ pl: 2, mt: 1 }}>
              {missingFields.map((field, index) => (
                <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <AlertCircle sx={{ fontSize: 16, mr: 1, color: 'warning.main' }} />
                  <Typography variant="body2">{field}</Typography>
                </Box>
              ))}
            </Box>
          </Alert>
        ) : (
          <Alert severity="success" icon={<CheckCircle />}>
            All required information has been provided.
          </Alert>
        )}

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit}
          disabled={!isComplete || isSubmitting}
          variant="contained"
          color="primary"
          endIcon={!isSubmitting && <ArrowRightIcon />}
          fullWidth
          sx={{ mt: 2, py: 1 }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Submitting...
            </>
          ) : (
            isComplete ? "Submit Information" : "Complete Required Fields"
          )}
        </Button>
      </Stack>
    </Box>
  );
};

export default InputFormContainer;